import { useState, useEffect, useMemo, createContext, Suspense } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "templates/Sidenav";
import Configurator from "templates/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
// (dark theme is hidden/disabled - light theme is always used)
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
// import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import GetCookies from "Cookies/getCookies";
import { AutoLogin } from "APIs/UserAuthenticationAPIs";
import SignIn from "layouts/authentication/sign-in";
import DashboardNavbar from "templates/Navbars/DashboardNavbar";
import MobileTopBar from "templates/Navbars/MobileTopBar";
import Swal from "sweetalert2";
import { themedDialog } from "components/Custom/AreYouSurePopUp";
import { Logout } from "APIs/UserAuthenticationAPIs";
import DashboardLayout from "templates/LayoutContainers/DashboardLayout";

import { useSelector, useDispatch } from "react-redux";
import useLocalStorage from "hooks/useLocalStorage";
import MDAlert from "components/MDAlert";
import MDTypography from "components/MDTypography";
export const UserContext = createContext();

import { Bounce, toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { setDarkMode } from "context";

export default function App() {
  // useEffect(() => {
  //   notify("Welcome back! ");
  // }, []);
  const [userData, setUserData] = useState(null);
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [localStorageData, setLocalStorageData] = useLocalStorage("userData", null);
  // Cache for the rtl
  // useMemo(() => {
  //   const cacheRtl = createCache({
  //     key: "rtl",
  //     stylisPlugins: [rtlPlugin],
  //   });

  //   setRtlCache(cacheRtl);
  // }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  // const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleDarkMode = () => setDarkMode(dispatch, !darkMode);

  // Always keep light theme (dark theme is hidden)
  useEffect(() => {
    if (darkMode) setDarkMode(dispatch, false);
  }, [darkMode]);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) => {
    return allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });
  };
  const themeButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right={{ xs: "1rem", sm: "2rem" }}
      bottom={{ xs: "1rem", sm: "2rem" }}
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleDarkMode}
      role="button"
      tabIndex={0}
      aria-label="Toggle dark mode"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleDarkMode();
        }
      }}
    >
      <Icon fontSize="small" color="inherit">
        {darkMode ? "light_mode" : "dark_mode"}
      </Icon>
    </MDBox>
  );

  const HandleAuthenticaton = async (access_token) => {
    const response = await AutoLogin(access_token);
    if (response !== null) {
      setIsLoggedIn(true);
      setLocalStorageData(response);
      setUserData((prev) => {
        return response;
      });
    } else {
      localStorage.removeItem("userData");
      setIsLoggedIn(false);
      setIsLoading(false);
      navigate("authentication/sign-in");
    }
  };
  // Ask for confirmation, then clear the session and return to the sign-in screen
  const handleLogout = async () => {
    const result = await Swal.fire({
      title: "Log out?",
      text: "You will need to sign in again to continue.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Log out",
      width: "40vh",
      ...themedDialog,
    });
    if (!result.isConfirmed) return;

    await Logout();
    setLocalStorageData(null);
    setUserData(null);
    setIsLoggedIn(false);
    navigate("/authentication/sign-in", { replace: true });
  };

  useEffect(() => {
    setIsLoading(true);
    if (localStorageData == null) {
      const access_token = GetCookies("userData");
      if (access_token !== undefined) {
        HandleAuthenticaton(JSON.parse(access_token));
      } else {
        navigate("authentication/sign-in");
        setIsLoading(false);
      }
      // location.reload();
    } else {
      setUserData((prev) => {
        return localStorageData;
      });
      setIsLoggedIn(true);
      setIsLoading(false);
    }
    setIsLoading(false);
  }, [isLoggedIn]);

  // const notification = useSelector((state) => state.notification.notificationBars);
  return (
    <>
      <Suspense
        fallback={
          <div className="d-flex justify-content-center align-item-center h-100">
            <span className="spinner-grow" role="status"></span>
          </div>
        }
      >
        <UserContext.Provider value={userData}>
          <ThemeProvider theme={theme}>
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
              transition={Bounce}
              style={{
                "--toastify-color-success": "#820000", // progress bar (maroon)
                "--toastify-icon-color-success": "#820000", // tick icon (maroon)
                "--toastify-text-color-light": "#4A0E0B", // message text (dark maroon)
                "--toastify-font-family": '"Poppins", sans-serif',
              }}
            />

            {isLoggedIn ? (
              <>
                <CssBaseline />

                <Sidenav
                  color={sidenavColor}
                  brand="https://api.minio.loweatelier.com/tuljamaa/static-images/admin-panel/Shriyantra-02.png"
                  brandName="Tulja Maa"
                  routes={routes}
                  onLogout={handleLogout}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
                <MobileTopBar title="Tulja Maa" />
                <DashboardLayout>
                  {/* <DashboardNavbar /> */}
                  <Routes>
                    {getRoutes(routes)}
                    <Route path="*" element={<Navigate to={"/devotee"} />} />
                  </Routes>
                  {/* <Configurator /> */}
                  {/* Dark mode toggle hidden - light theme only */}
                  {/* {themeButton} */}
                  {/* <div
                    style={{
                      zIndex: 100000000,
                      position: "fixed",
                      top: 10,
                      left: "50%",
                      transform: "translate(-50%, 0)",
                    }}
                  >
                    {notification?.map((item, key) => {
                      return (
                        <MDBox key={key}>
                          <MDAlert
                            color={item.color}
                            className="mt-2"
                            dismissible
                            style={{ height: "40px", zIndex: 10000000, minWidth: "350px" }}
                          >
                            <Icon fontSize="large">
                              {" "}
                              {item.color == "success" ? " done " : item.color}{" "}
                            </Icon>{" "}
                            <pre> </pre>
                            <MDTypography color="white" fontSize="auto" fullWidth>
                              {item.text}
                            </MDTypography>
                          </MDAlert>
                        </MDBox>
                      );
                    })}
                  </div> */}
                </DashboardLayout>
              </>
            ) : (
              <>
                {isLoading ? (
                  <div className="d-flex justify-content-center align-items-center vh-100">
                    <div className="spinner-grow"></div>
                  </div>
                ) : (
                  <Routes>
                    <Route
                      path="*"
                      element={
                        <SignIn
                          ChangeLoggedIn={(e) => {
                            setIsLoggedIn(e);
                          }}
                        />
                      }
                    />
                  </Routes>
                )}
              </>
            )}
          </ThemeProvider>
        </UserContext.Provider>
      </Suspense>
    </>
  );
}

export const notify = (text, theme) => {
  if (theme == "success") {
    toast.success(text, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light" || theme,
      transition: Bounce,
    });
  } else if (theme == "error") {
    toast.error(text, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light" || theme,
      transition: Bounce,
    });
  } else {
    toast(text, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light" || theme,
      transition: Bounce,
    });
  }
};
